// hooks/useSchedules.js
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { scheduleApiService } from '../Api/scheduleApiService';
import drEasyApi from '../Api/Customapi';
// hooks/usePatientData.js

export const usePatientData = (patientId) => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    
    const loadAnswers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await drEasyApi.get('/api/Book/GetPatientFormAnswers', {
          params: { userId: patientId },
        });
        
        const answersArray = response.data?.answers ?? [];
        setAnswers(answersArray);
      } catch (err) {
        console.error('Error loading patient answers:', err);
        setError(err);
        setAnswers([]);
      } finally {
        setLoading(false);
      }
    };

    loadAnswers();
  }, [patientId]);

  return { answers, loading, error };
};


export const useSchedules = (patientId) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSchedules = useCallback(async () => {
    if (!patientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await scheduleApiService.getUserSchedules(patientId);
      setSchedules(data);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError(err);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const createSchedule = async (scheduleData) => {
    try {
      await scheduleApiService.assignSchedule({ ...scheduleData, userId: patientId });
      message.success('Schedule created successfully');
      await loadSchedules();
    } catch (err) {
      console.error('Error creating schedule:', err);
      message.error(err.response.data[0] || 'Failed to create schedule');
      throw err;
    }
  };

  const updateSchedule = async (scheduleId, scheduleData) => {
    try {
      await scheduleApiService.editSchedule({ ...scheduleData, id: scheduleId });
      message.success('Schedule updated successfully');
      await loadSchedules();
    } catch (err) {
      console.error('Error updating schedule:', err);
      message.error(err.response.data[0] || 'Failed to update schedule');
      throw err;
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      await scheduleApiService.deleteSchedule(scheduleId);
      message.success('Schedule deleted successfully');
      await loadSchedules();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      message.error(err.response.data[0] || 'Failed to delete schedule');
      throw err;
    }
  };

  return {
    schedules,
    loading,
    error,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    refreshSchedules: loadSchedules
  };
};

// hooks/useReportHistory.js
export const useReportHistory = (patientId) => {
  const [reportHistory, setReportHistory] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadReportHistory = useCallback(async (pageNumber = 1, pageSize = 10) => {
    if (!patientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await drEasyApi.get('/api/DrEasyAi/GetSummrizeHistory', {
        params: {
          UserId: patientId,
          PageNumber: pageNumber,
          PageSize: pageSize
        }
      });
      
      const data = response.data;
      if (data && data.items) {
        setReportHistory(data.items);
        setPagination({
          current: data.pageNumber || pageNumber,
          pageSize: data.pageSize || pageSize,
          total: data.totalCount || 0
        });
      } else {
        setReportHistory([]);
        setPagination({
          current: 1,
          pageSize: 10,
          total: 0
        });
      }
    } catch (err) {
      console.error('Error loading report history:', err);
      setError(err);
      message.error('Failed to load report history');
      setReportHistory([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadReportHistory();
  }, [loadReportHistory]);

  return {
    reportHistory,
    pagination,
    loading,
    error,
    loadReportHistory
  };
};